import { Button } from "@shared/ui";
import Permission from "../components/Permission";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";

import { basePermissions } from "../lib/constants";
import { useEffect } from "react";
import _ from "lodash";

const Permissions = ({ step, nextStep, client, posthog, session }) => {
  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, [posthog]);

  // Handle the continue button click
  const handleContinue = () => {
    nextStep("carriers");
  };

  // Get configuration from session
  const customPermissions = _(session).get(
    "config.scopes.permissions",
    undefined
  );
  const monitoring = _(session).get("config.scopes.monitoring", false);
  const policyActions = _(session).get("config.scopes.policyActions", []);
  const policyTypes = _(session).get("config.scopes.policyTypes", []);
  const vehiclePolicyTypes = ["auto", "motorcycle", "trailer", "boat", "rv"];
  const dwellingPolicyTypes = ["home", "condo", "tenant", "landlord"];

  // Start with base permissions for viewing policy data
  let viewPermissions = [...basePermissions];
  let managePermissions = [];

  // If client has custom permissions, merge them with base permissions
  if (customPermissions) {
    // Merge custom view permissions with base permissions
    viewPermissions = _.unionBy(customPermissions, basePermissions, "value");
  }

  // Add monitoring to manage permissions if enabled
  if (monitoring) {
    managePermissions.push({
      order: 11,
      name: "Policy updates",
      icon: "monitoring",
      value: "monitoring",
    });
  }

  // Add policy actions to manage permissions
  if (policyActions && policyActions.length > 0) {
    policyActions.forEach((action, index) => {
      let actionName = "";
      if (action.action === "third-party-update") {
        actionName = "Lienholder updates";
      }

      if (actionName) {
        managePermissions.push({
          order: 12 + index,
          name: actionName,
          icon: "bank",
          value: action.action,
        });
      }
    });
  }

  // Add properties or vehicles or dwellings based on policy types
  if (
    policyTypes.some((i) => vehiclePolicyTypes.includes(i)) &&
    policyTypes.some((i) => dwellingPolicyTypes.includes(i))
  ) {
    viewPermissions = [
      ...viewPermissions,
      {
        order: 6,
        name: "Properties",
        icon: "key",
        value: "properties",
      },
    ];
  } else if (policyTypes.some((i) => vehiclePolicyTypes.includes(i))) {
    viewPermissions = [
      ...viewPermissions,
      {
        order: 6,
        name: "Vehicles",
        icon: "wheel",
        value: "properties",
      },
    ];
  } else if (policyTypes.some((i) => dwellingPolicyTypes.includes(i))) {
    viewPermissions = [
      ...viewPermissions,
      {
        order: 6,
        name: "Dwellings",
        icon: "house",
        value: "properties",
      },
    ];
  } else {
    viewPermissions = [
      ...viewPermissions,
      {
        order: 6,
        name: "Properties",
        icon: "key",
        value: "properties",
      },
    ];
  }

  // Sort both sections by order
  viewPermissions = _.sortBy(viewPermissions, ["order"], ["asc"]);
  managePermissions = _.sortBy(managePermissions, ["order"], ["asc"]);

  // Combine view and manage permissions for existing view
  const perms = [...viewPermissions, ...managePermissions];

  return (
    <>
      <StepBody>
        <StepHeading
          title={
            <span className="font-normal">
              <strong className="font-semibold">{client.displayName}</strong>{" "}
              will receive the following access to your policy:
            </span>
          }
        />
        <ul
          className="divide-y divide-subtle rounded-lg border border-subtle"
          aria-label="List of permissions"
        >
          {_(perms)
            .chain()
            .orderBy(["order"], ["asc"])
            .map((perm) => (
              <Permission key={perm.order} name={perm.name} icon={perm.icon} />
            ))
            .value()}
        </ul>
      </StepBody>
      <StepFooter>
        <Button onClick={handleContinue}>Continue</Button>
      </StepFooter>
    </>
  );
};

export default Permissions;
